/**
 * As duas regras do formulario de editar a ficha, puras para terem teste.
 *
 * 1. O formulario ABRE com o que e da unidade, e nao com o que ela herda. Se
 *    abrisse com os 300 L herdados, gravar sem mexer em nada copiaria o valor
 *    para a maquina, e ela deixaria de acompanhar a configuracao em silencio.
 * 2. O que fica EM BRANCO vai em `limpar`, pelo nome. O `PATCH` usa
 *    `coalesce`, e nulo sozinho nao apaga nada.
 */
import { interpretarNumero } from '@/lib/formato';
import { fraseDoRebase, rebasear } from '@/lib/versao-da-ficha';

import type { AjusteDaFicha, CampoQueSeApaga } from './consultas-frota';
import { COMBUSTIVEIS, type Combustivel, type EquipamentoDaFrota } from './frota';

export interface EstadoDaFicha {
  /** Só para mostrar: o código é do sistema e o ajuste nunca o muda (0131). */
  readonly codigo: string;
  readonly descricao: string;
  readonly configuracaoId: string;
  readonly filialId: string;
  readonly numeroSerie: string;
  readonly motorModelo: string;
  readonly motorNumeroSerie: string;
  readonly ano: string;
  /** `''` herda; `'horimetro'` ou `'odometro'` e da unidade. */
  readonly medidor: string;
  /** `''` herda o tipo do MODELO; escrito e o tipo desta maquina (0124), um da lista (0131). */
  readonly tipo: string;
  readonly capacidade: string;
  /* 0060 */
  readonly patrimonio: string;
  /** 0125 — o codigo do dia a dia: `''` e "nao tem". */
  readonly codigoInterno: string;
  /** `''` e "nao informado"; senao, um de `COMBUSTIVEIS`. */
  readonly combustivel: string;
  /** 0133: `'sim'` abastece em posto parceiro; `''` nao. Texto, como todo campo da ficha, para o rebase. */
  readonly abasteceEmPosto: string;
  readonly anoModelo: string;
  readonly anoAquisicao: string;
  readonly placa: string;
  readonly renavam: string;
}

export function fichaInicial(e: EquipamentoDaFrota): EstadoDaFicha {
  return {
    codigo: e.codigo,
    descricao: e.descricao,
    configuracaoId: e.configuracaoId ?? '',
    filialId: e.filialId ?? '',
    numeroSerie: e.numeroSerie ?? '',
    motorModelo: e.origemDoMotor === 'unidade' ? (e.motorModelo ?? '') : '',
    motorNumeroSerie: e.motorNumeroSerie ?? '',
    ano: e.anoFabricacao === null ? '' : String(e.anoFabricacao),
    medidor: e.origemDoMedidor === 'unidade' ? e.tipoMedidor : '',
    tipo: e.origemDoTipo === 'unidade' ? (e.tipo ?? '') : '',
    capacidade:
      e.origemDaCapacidade === 'unidade' && e.capacidadeTanqueL !== null
        ? String(e.capacidadeTanqueL).replace('.', ',')
        : '',
    patrimonio: e.patrimonio ?? '',
    codigoInterno: e.codigoInterno ?? '',
    combustivel: e.combustivel ?? '',
    abasteceEmPosto: e.abasteceEmPosto ? 'sim' : '',
    anoModelo: e.anoModelo === null ? '' : String(e.anoModelo),
    anoAquisicao: e.anoAquisicao === null ? '' : String(e.anoAquisicao),
    placa: e.placa ?? '',
    renavam: e.renavam ?? '',
  };
}

/* ------------------------------------------------------------------------
   Os seis campos da 0060, com as regras que a tela e o servidor repetem. O
   cadastro e a edicao da ficha usam as mesmas funcoes: a frase do erro e o
   que vai ao servidor nao podem divergir entre as duas telas.
   ------------------------------------------------------------------------ */

const ERRO_DO_ANO = 'Informe um ano entre 1950 e 2100, ou deixe em branco.';
const ERRO_DA_PLACA = 'A placa tem 3 letras e 4 caracteres: ABC1234 ou, no padrão Mercosul, ABC1D23.';
const ERRO_DO_RENAVAM = 'O Renavam tem 11 dígitos.';
const ERRO_DO_CODIGO_INTERNO = 'O código interno usa letras maiúsculas, números e traços: CB-07.';

/** O mesmo formato do `check` da 0060: antiga (ABC1234) ou Mercosul (ABC1D23). */
const FORMATO_DA_PLACA = /^[A-Z]{3}[0-9][A-Z0-9][0-9]{2}$/;

/** "abc-1d23" vira "ABC1D23": sem hifen, sem espaco, em caixa alta. */
export function normalizarPlaca(valor: string): string {
  return valor.replace(/[\s-]/g, '').toUpperCase();
}

export function erroDaPlaca(valor: string): string | null {
  const placa = normalizarPlaca(valor);
  return placa === '' || FORMATO_DA_PLACA.test(placa) ? null : ERRO_DA_PLACA;
}

/**
 * Sem espaco, ponto e hifen, com o zero a esquerda completado ate 11: a
 * planilha guarda o Renavam como numero e perde o zero — "1234567890" e o
 * 01234567890. So de 9 ou 10 digitos, como no servidor: o Renavam antigo tinha
 * 9, e e o maximo que o zero a esquerda explica. "123" e Renavam errado.
 */
export function normalizarRenavam(valor: string): string {
  const digitos = valor.replace(/[\s.-]/g, '');
  return /^\d{9,10}$/.test(digitos) ? digitos.padStart(11, '0') : digitos;
}

export function erroDoRenavam(valor: string): string | null {
  const renavam = normalizarRenavam(valor);
  return renavam === '' || /^\d{11}$/.test(renavam) ? null : ERRO_DO_RENAVAM;
}

/**
 * O codigo interno (0125), no padrao que o dono pediu: letra maiuscula,
 * algarismo e traco. O mesmo `check` do banco, com a frase aqui.
 *
 * O espaco SAI em vez de virar erro — "CB 07" colado da planilha e a mesma
 * maquina que "CB07" —, e a caixa sobe sozinha: quem digita "cb-07" grava
 * "CB-07", como o resto do sistema faz desde a 0049.
 */
const FORMATO_DO_CODIGO_INTERNO = /^[A-Z0-9-]+$/;

export function normalizarCodigoInterno(valor: string): string {
  return valor.replace(/\s/g, '').toUpperCase();
}

export function erroDoCodigoInterno(valor: string): string | null {
  const codigo = normalizarCodigoInterno(valor);
  return codigo === '' || FORMATO_DO_CODIGO_INTERNO.test(codigo) ? null : ERRO_DO_CODIGO_INTERNO;
}

/** `null` em branco; `NaN` quando escrito e nao e um ano que se possa gravar. */
function lerAno(valor: string): number | null {
  if (valor.trim() === '') return null;
  const n = interpretarNumero(valor);
  return n !== null && Number.isInteger(n) && n >= 1950 && n <= 2100 ? n : Number.NaN;
}

export function erroDoAno(valor: string): string | null {
  return Number.isNaN(lerAno(valor)) ? ERRO_DO_ANO : null;
}

/** O ano do modelo e o de fabricacao ou o seguinte. Sem fabricacao, so a faixa. */
export function erroDoAnoModelo(modelo: string, fabricacao: string): string | null {
  const m = lerAno(modelo);
  if (Number.isNaN(m)) return ERRO_DO_ANO;
  const f = lerAno(fabricacao);
  if (m === null || f === null || Number.isNaN(f)) return null;
  return m === f || m === f + 1
    ? null
    : `O ano do modelo é o de fabricação ou o seguinte: informe ${String(f)} ou ${String(f + 1)}.`;
}

/** A maquina nao e comprada antes de fabricada. Sem fabricacao, so a faixa. */
export function erroDoAnoAquisicao(aquisicao: string, fabricacao: string): string | null {
  const a = lerAno(aquisicao);
  if (Number.isNaN(a)) return ERRO_DO_ANO;
  const f = lerAno(fabricacao);
  if (a === null || f === null || Number.isNaN(f)) return null;
  return a >= f ? null : `A aquisição não vem antes da fabricação: informe ${String(f)} ou um ano depois.`;
}

export interface CamposDaFichaComplementar {
  readonly patrimonio: string;
  readonly codigoInterno: string;
  readonly combustivel: string;
  readonly anoModelo: string;
  readonly anoAquisicao: string;
  readonly placa: string;
  readonly renavam: string;
}

export interface FichaComplementar {
  readonly patrimonio: string | null;
  readonly codigoInterno: string | null;
  readonly combustivel: Combustivel | null;
  readonly anoModelo: number | null;
  readonly anoAquisicao: number | null;
  readonly placa: string | null;
  readonly renavam: string | null;
}

/**
 * Os seis campos como o servidor os recebe — placa e Renavam ja normalizados,
 * branco como `null`. Nulo quando algum deles nao pode ser gravado: a tela ja
 * mostra o erro do campo, e o botao fica desligado.
 */
export function lerFichaComplementar(campos: CamposDaFichaComplementar, anoFabricacao: string): FichaComplementar | null {
  if (
    erroDoCodigoInterno(campos.codigoInterno) ||
    erroDaPlaca(campos.placa) ||
    erroDoRenavam(campos.renavam) ||
    erroDoAnoModelo(campos.anoModelo, anoFabricacao) ||
    erroDoAnoAquisicao(campos.anoAquisicao, anoFabricacao)
  ) {
    return null;
  }
  const combustivel = COMBUSTIVEIS.find((c) => c.valor === campos.combustivel)?.valor ?? null;
  if (campos.combustivel !== '' && combustivel === null) return null;
  return {
    patrimonio: campos.patrimonio.trim() || null,
    codigoInterno: normalizarCodigoInterno(campos.codigoInterno) || null,
    combustivel,
    anoModelo: lerAno(campos.anoModelo),
    anoAquisicao: lerAno(campos.anoAquisicao),
    placa: normalizarPlaca(campos.placa) || null,
    renavam: normalizarRenavam(campos.renavam) || null,
  };
}

/**
 * O pedido do `PATCH`. Nulo quando o estado nao pode ser gravado (descricao
 * em branco, ano fora de 1950–2100, capacidade que nao e positiva,
 * placa ou Renavam fora do formato, ano do modelo ou de aquisicao que nao
 * combina com o de fabricacao) —
 * a tela ja mostra o erro de cada campo, e o botao fica desligado.
 */
export function montarAjuste(estado: EstadoDaFicha): AjusteDaFicha | null {
  /* O codigo nao vai (0131, decisao 57): e do sistema, e o `PATCH` nunca o muda. */
  const descricao = estado.descricao.trim();
  if (!descricao) return null;

  const ano = estado.ano.trim() === '' ? null : interpretarNumero(estado.ano);
  if (estado.ano.trim() !== '' && (ano === null || ano < 1950 || ano > 2100)) return null;
  const capacidade = estado.capacidade.trim() === '' ? null : interpretarNumero(estado.capacidade);
  if (estado.capacidade.trim() !== '' && (capacidade === null || capacidade <= 0)) return null;
  const complementar = lerFichaComplementar(estado, estado.ano);
  if (!complementar) return null;

  const limpar: CampoQueSeApaga[] = [];
  const preenchidos: Partial<Record<CampoQueSeApaga, string | number>> = {};
  const colocar = (campo: CampoQueSeApaga, valor: string | number | null) => {
    if (valor === null || valor === '') limpar.push(campo);
    else preenchidos[campo] = valor;
  };

  colocar('configuracaoId', estado.configuracaoId.trim());
  colocar('filialId', estado.filialId.trim());
  colocar('numeroSerie', estado.numeroSerie.trim());
  colocar('motorModelo', estado.motorModelo.trim());
  colocar('motorNumeroSerie', estado.motorNumeroSerie.trim());
  colocar('anoFabricacao', ano);
  colocar('tipoMedidor', estado.medidor === 'horimetro' || estado.medidor === 'odometro' ? estado.medidor : null);
  colocar('tipo', estado.tipo.trim());
  colocar('capacidadeTanqueL', capacidade);
  colocar('patrimonio', complementar.patrimonio);
  colocar('codigoInterno', complementar.codigoInterno);
  colocar('combustivel', complementar.combustivel);
  colocar('anoModelo', complementar.anoModelo);
  colocar('anoAquisicao', complementar.anoAquisicao);
  colocar('placa', complementar.placa);
  colocar('renavam', complementar.renavam);

  /* 0133: a marca vai sempre, ligada ou desligada — desmarcar e um valor,
     nao um campo em branco. */
  return { descricao, ...preenchidos, abasteceEmPosto: estado.abasteceEmPosto === 'sim', limpar } as AjusteDaFicha;
}

/** O rotulo de cada campo do formulario, como a tela o escreve. */
export const ROTULOS_DA_FICHA: Readonly<Record<keyof EstadoDaFicha, string>> = {
  codigo: 'Código',
  descricao: 'Descrição',
  configuracaoId: 'Configuração',
  filialId: 'Filial',
  numeroSerie: 'Número de série',
  motorModelo: 'Motor',
  motorNumeroSerie: 'Número de série do motor',
  ano: 'Ano de fabricação',
  medidor: 'Medidor',
  tipo: 'Tipo',
  capacidade: 'Capacidade do tanque',
  patrimonio: 'Patrimônio',
  codigoInterno: 'Código interno',
  combustivel: 'Combustível',
  abasteceEmPosto: 'Abastece em posto parceiro',
  anoModelo: 'Ano do modelo',
  anoAquisicao: 'Ano de aquisição',
  placa: 'Placa',
  renavam: 'Renavam',
};

/**
 * A ficha que outra pessoa gravou enquanto o formulario estava aberto
 * (robos de 20/10/2026, achado 1): o servidor recusou com `ficha_alterada`, a
 * tela releu a maquina, e aqui o formulario e refeito por cima da versao nova
 * — o campo que so a outra pessoa mudou vem com o valor dela, o que voce
 * digitou fica — e a frase diz quais. `nomeDoValor` troca o id da
 * configuracao e da filial pelo nome que a lista mostra.
 */
export function refazerSobreAFichaNova(
  meu: EstadoDaFicha,
  base: EstadoDaFicha,
  nova: EquipamentoDaFrota,
  nomeDoValor: (campo: keyof EstadoDaFicha, valor: string) => string = (_c, v) => v,
): { readonly estado: EstadoDaFicha; readonly base: EstadoDaFicha; readonly frase: string } {
  const novaBase = fichaInicial(nova);
  const r = rebasear(meu, base, novaBase);
  return { estado: r.estado, base: novaBase, frase: fraseDoRebase(r, ROTULOS_DA_FICHA, nomeDoValor) };
}
